<template>
	<teleport to="#app">
		<BaseDialog
			:isOpen="isVisible"
			@close="close"
			@runAfterLeave="runAfterLeave"
		>
			<template #default>
				<Transition
					enter-active-class="transition-opacity duration-400 ease-out"
					enter-from-class="opacity-0"
					enter-to-class="opacity-100"
					leave-active-class="transition-opacity duration-300 ease-in"
					leave-from-class="opacity-100"
					leave-to-class="opacity-0"
				>
					<div
						v-if="isVisible"
						class="fixed inset-0 flex items-center justify-center z-50 bg-black/20 antialiased"
						@click="close"
					>
						<Transition
							enter-active-class="transition-all duration-500 ease-out"
							enter-from-class="opacity-0 translate-y-4"
							enter-to-class="opacity-100 translate-y-0"
							leave-active-class="transition-all duration-300 ease-in"
							leave-from-class="opacity-100 translate-y-0"
							leave-to-class="opacity-0 translate-y-4"
							appear
						>
							<div
								v-if="isVisible"
								ref="modalContainer"
								class="relative flex flex-col h-[90vh] w-[98vw] lg:w-[88vw] bg-white rounded-xl overflow-hidden shadow-lg transform-gpu"
								@click.stop
							>
								<Transition
									enter-active-class="transition-all duration-600 ease-out delay-150"
									enter-from-class="opacity-0 translate-y-3"
									enter-to-class="opacity-100 translate-y-0"
									appear
								>
									<header
										v-if="isVisible"
										class="top-0 z-10 flex items-center mx-auto py-4 md:px-8 lg:px-3 relative bg-white"
									>
										<button
											v-for="driver in valueDrivers"
											:key="driver.id"
											@click="activeDriverId = driver.id"
											class="last:mr-5 **:cursor-pointer cursor-pointer flex flex-col items-center md:px-4 lg:px-6 py-2 transition-all duration-200 relative bg-transparent border-r-1 border-neutral-200 last:border-r-0 outline-none"
											style="background: none"
										>
											<div
												:class="[
													'flex flex-col lg:flex-row items-center gap-2 transition-all duration-200 cursor-pointer',
													isActiveTab(driver)
														? 'text-blue-500 font-bold'
														: 'text-slate-500 font-normal hover:text-slate-950',
												]"
											>
												<img
													:src="`/images/value-drivers/${driver.id}.svg`"
													:alt="driver.title"
													class="h-10 w-10 mx-4 md:mx-0 md:w-6 md:h-6 lg:block transition-transform duration-200 object-contain"
													:style="{
														filter: isActiveTab(driver)
															? 'brightness(0) saturate(100%) invert(27%) sepia(51%) saturate(2878%) hue-rotate(200deg) brightness(104%) contrast(97%)'
															: 'opacity(0.6)'
													}"
												/>
												<span
													class="hidden md:block text-xs lg:text-sm text-center"
													>{{ driver.name }}</span
												>
											</div>
											<Transition
												enter-active-class="transition-all duration-300 ease-out"
												enter-from-class="opacity-0 scale-x-0"
												enter-to-class="opacity-100 scale-x-100"
												leave-active-class="transition-all duration-200 ease-in"
												leave-from-class="opacity-100 scale-x-100"
												leave-to-class="opacity-0 scale-x-0"
											>
												<div
													v-if="isActiveTab(driver)"
													class="h-1 w-5/6 rounded bg-blue-500 absolute -bottom-4 origin-center"
													style="min-width: 40px"
												></div>
											</Transition>
										</button>
									</header>
								</Transition>

								<span class="border-b border-neutral-200"></span>

								<!-- Close button  -->
								<button
									type="button"
									@click="close"
									class="absolute right-2 top-2 md:top-3 cursor-pointer p-2 hover:bg-slate-50 rounded-xl transition-all duration-200 group hover:scale-105 active:scale-95"
									aria-label="Close value driver modal"
								>
									<svg
										xmlns="http://www.w3.org/2000/svg"
										width="30"
										height="31"
										viewBox="0 0 30 31"
										fill="none"
										class="transition-transform duration-200"
									>
										<mask
											id="mask0_2008_32870"
											style="mask-type: alpha"
											maskUnits="userSpaceOnUse"
											x="0"
											y="0"
											width="30"
											height="31"
										>
											<rect y="0.5" width="30" height="30" fill="#D9D9D9" />
										</mask>
										<g mask="url(#mask0_2008_32870)">
											<path
												d="M8 24.25L6.25 22.5L13.25 15.5L6.25 8.5L8 6.75L15 13.75L22 6.75L23.75 8.5L16.75 15.5L23.75 22.5L22 24.25L15 17.25L8 24.25Z"
												fill="#6B6B6B"
												class="transition-colors duration-200 group-hover:fill-slate-700"
											/>
										</g>
									</svg>
								</button>

								<Transition
									enter-active-class="transition-all duration-700 ease-out delay-250"
									enter-from-class="opacity-0 translate-y-4"
									enter-to-class="opacity-100 translate-y-0"
									appear
								>
									<div
										v-if="isVisible"
										ref="scrollContainer"
										class="flex flex-col md:flex-row gap-8 lg:gap-10 px-6 py-8 md:p-10 md:pr-12 md:pt-10 md:pl-4 overflow-auto flex-1"
									>
										<Transition
											enter-active-class="transition-all duration-600 ease-out delay-350"
											enter-from-class="opacity-0 translate-y-3"
											enter-to-class="opacity-100 translate-y-0"
											appear
										>
											<aside
												v-if="isVisible && activeDriver.id"
												class="flex-shrink-0 w-full md:w-2/6 lg:w-1/5 flex flex-col items-center"
											>
												<img
													:src="`/images/value-drivers/${activeDriver.id}.svg`"
													:alt="activeDriver.title"
													class="h-25 w-25 md:w-50 md:h-50 mb-4 transition-transform duration-300 hover:scale-105 object-contain"
												/>
											</aside>
										</Transition>

										<Transition
											enter-active-class="transition-all duration-600 ease-out delay-450"
											enter-from-class="opacity-0 translate-y-3"
											enter-to-class="opacity-100 translate-y-0"
											appear
										>
											<section v-if="isVisible && activeDriver.id" class="flex-1">
												<Transition
													enter-active-class="transition-all duration-600 ease-out delay-550"
													enter-from-class="opacity-0 translate-y-3"
													enter-to-class="opacity-100 translate-y-0"
													appear
												>
													<h1
														v-if="isVisible"
														class="text-2xl lg:text-3xl font-bold mb-6 md:mb-10"
													>
														{{ activeDriver.name }}
													</h1>
												</Transition>

												<Transition
													enter-active-class="transition-all duration-600 ease-out delay-650"
													enter-from-class="opacity-0 translate-y-3"
													enter-to-class="opacity-100 translate-y-0"
													appear
												>
													<div
														v-if="isVisible"
														class="text-sm font-light text-slate-700"
														v-html="activeDriver.description"
													></div>
												</Transition>

												<Transition
													enter-active-class="transition-all duration-600 ease-out delay-750"
													enter-from-class="opacity-0 translate-y-2"
													enter-to-class="opacity-100 translate-y-0"
													appear
												>
													<hr
														v-if="isVisible"
														:class="(activeDriver.related_solutions?.length ?? 0) > 0 ? 'my-12 border-neutral-200' : 'pb-20 border-none'"
													/>
												</Transition>

												<Transition
													enter-active-class="transition-all duration-600 ease-out delay-850"
													enter-from-class="opacity-0 translate-y-3"
													enter-to-class="opacity-100 translate-y-0"
													appear
												>
													<div v-if="isVisible && (activeDriver.related_solutions?.length ?? 0) > 0">
														<h4
															class="mb-7 text-lg text-slate-950 font-semibold capitalize"
														>
															{{ getTranslations("related solutions") }}
														</h4>
														<div class="columns-2 lg:columns-3 pb-4 lg:pb-12">
															<div
																v-for="related in activeDriver.related_solutions"
																:key="related.slug"
																class="transition-transform duration-200 hover:scale-[1.02] lg:max-w-58"
															>
																<RouterLink
																	v-if="related.slug"
																	:to="`${baseURL}/${related.slug}`"
																	@click="close"
																	class="block mb-2 mr-9 group"
																>
																	<p
																		v-html="related.title"
																		class="text-sm text-slate-700 font-medium antialiased hover:text-blue-400 leading-tight md:leading-normal transition-colors duration-200"
																	></p>
																</RouterLink>
															</div>
														</div>
													</div>
												</Transition>
											</section>
										</Transition>
									</div>
								</Transition>
							</div>
						</Transition>
					</div>
				</Transition>
			</template>
		</BaseDialog>
	</teleport>
</template>

<script src="./ValueDriverModal"></script>